/**
 * ManageSystemsPage - SmartPark management interface
 *
 * Supports both admin and user modes with configurable columns and filters.
 *
 * To add a new column:
 * 1. Add the column key to the ColumnKey type
 * 2. Define the column config in COLUMN_CONFIGS (or handle dynamically for renderCell columns)
 * 3. If editable, add mutation logic in handleSave
 * 4. Update the MachineRow type if needed for computed fields
 *
 * @example Admin usage
 * <ManageSystemsPage
 *   isTaggingMachines={false}
 *   filters={["region", "search"]}
 *   columns={["name", "machineUuid", "region", "status", "auraAccess", "config"]}
 * />
 *
 * @example User usage
 * <ManageSystemsPage
 *   isTaggingMachines={true}
 *   filters={["search"]}
 *   columns={["name", "region", "auraAccess", "config"]}
 * />
 */

import {
  getMachinesSearchOptions,
  getMachinesSearchTaggingOptions,
  MachinePublicHydrated,
  postMachinesUpdateGroupsByGroupByAction,
  postMachinesUpdateStatus,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Input, Label, Select, Spinner, toast } from "@win-reality/react-ui";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";

import type { EditableColumnDef } from "@/components/EditableTable";
import { EditableTable } from "@/components/EditableTable";
import { TableCell } from "@/components/ui/Table";
import { getComplexName } from "@/hooks/useComplexes";
import { useMachines } from "@/hooks/useMachines";
import { useRegions } from "@/hooks/useRegions";
import { ROUTES } from "@/lib/routes";

export type MachineRow = MachinePublicHydrated & {
  auraAccess?: string;
};

type ColumnKey =
  | "name"
  | "machineUuid"
  | "region"
  | "status"
  | "auraAccess"
  | "config";
type FilterKey = "region" | "search";

interface ManageSystemsPageProps {
  isTaggingMachines: boolean;
  filters: FilterKey[];
  columns: ColumnKey[];
}

const formatStatus = (value: unknown) => {
  if (!value) return "-";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

const COLUMN_CONFIGS: Partial<
  Record<ColumnKey, EditableColumnDef<MachineRow>>
> = {
  name: {
    key: "name",
    label: "Name",
    editable: false,
  },
  machineUuid: {
    key: "machineUuid",
    label: "SmartPark ID",
    editable: false,
    widthClass: "w-64",
  },
  region: {
    key: "region",
    label: "Region",
    editable: false,
  },
  status: {
    key: "status",
    label: "Status",
    editable: true,
    editor: "select",
    options: [
      { value: "active", label: "Active", id: "active" },
      { value: "inactive", label: "Inactive", id: "inactive" },
    ],
    format: formatStatus,
  },
  auraAccess: {
    key: "auraAccess",
    label: "Public Aura Access",
    editable: true,
    editor: "select",
    options: [
      { value: "on", label: "On", id: "on" },
      { value: "off", label: "Off", id: "off" },
    ],
    format: formatStatus,
  },
};

export const ManageSystemsPage = ({
  isTaggingMachines,
  filters,
  columns: visibleColumns,
}: ManageSystemsPageProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const itemsPerPage = 15;
  const showRegionFilter = filters.includes("region");
  const showSearchFilter = filters.includes("search");

  const { regions } = useRegions({
    enabled: showRegionFilter,
  });

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery);
    },
    500,
    [searchQuery],
  );

  const trimmedSearch = debouncedSearchQuery.trim();

  const {
    machines,
    loadingMachines,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useMachines({
    region:
      showRegionFilter && selectedRegion !== "all" ? selectedRegion : undefined,
    isTaggingMachines,
    searchQuery: trimmedSearch || undefined,
  });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedRegion, debouncedSearchQuery]);

  // Auto-fetch all search result pages
  useEffect(() => {
    if (trimmedSearch && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [trimmedSearch, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      machineUuid,
      status,
    }: {
      machineUuid: string;
      status: string;
    }) => {
      const { data } = await postMachinesUpdateStatus({
        query: { machineUuid, status },
      });
      return data;
    },
    onError: (error) => {
      console.error("Failed to update system status:", error);
    },
  });

  const updateAuraAccessMutation = useMutation({
    mutationFn: async ({
      machineUuid,
      auraAccess,
    }: {
      machineUuid: string;
      auraAccess: string;
    }) => {
      const action = auraAccess === "on" ? "add" : "remove";
      const { data } = await postMachinesUpdateGroupsByGroupByAction({
        path: { group: "aura", action },
        query: { machineUuid },
      });
      return data;
    },
    onError: (error) => {
      console.error("Failed to update aura access:", error);
    },
  });

  const regionOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Regions" }];
    if (regions) {
      options.push(
        ...regions.map((region: string) => ({ value: region, label: region })),
      );
    }
    return options;
  }, [regions]);

  const filteredMachines = useMemo(() => {
    if (!machines) return [];

    return (machines as MachinePublicHydrated[]).map((machine) => ({
      ...machine,
      auraAccess: machine.groupIds?.includes("aura") ? "on" : "off",
    }));
  }, [machines]);

  const paginatedMachines = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    return filteredMachines.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredMachines, currentPage]);

  const configColumn: EditableColumnDef<MachineRow> = useMemo(
    () => ({
      key: "config" as keyof MachineRow,
      label: "",
      editable: false,
      renderCell: (row: MachineRow) => (
        <TableCell className="px-4 py-3 font-medium">
          <button
            className="text-blue text-sm hover:underline whitespace-nowrap"
            onClick={() => {
              const configRoute = ROUTES.MANAGE_FIELD.replace(
                ":complexName",
                getComplexName(row),
              ).replace(":fieldId", row.machineUuid);
              navigate(configRoute, { state: { fromManageSystems: true } });
            }}
          >
            Configure
          </button>
        </TableCell>
      ),
    }),
    [navigate],
  );

  const columns = useMemo(
    () =>
      visibleColumns.map((key) => {
        if (key === "config") return configColumn;
        return COLUMN_CONFIGS[key]!;
      }),
    [visibleColumns, configColumn],
  );

  const originalMachinesMap = useMemo(
    () => new Map(filteredMachines.map((m) => [m.machineUuid, m])),
    [filteredMachines],
  );

  const totalPages = Math.ceil(filteredMachines.length / itemsPerPage);

  const handleSave = async (changedRows: MachineRow[]) => {
    setIsSaving(true);

    try {
      const updates = changedRows.flatMap((machine) => {
        const promises: Promise<unknown>[] = [];
        const original = originalMachinesMap.get(machine.machineUuid);

        if (!original) return promises;

        if (machine.status && machine.status !== original.status) {
          promises.push(
            updateStatusMutation.mutateAsync({
              machineUuid: machine.machineUuid,
              status: machine.status,
            }),
          );
        }

        if (machine.auraAccess && machine.auraAccess !== original.auraAccess) {
          promises.push(
            updateAuraAccessMutation.mutateAsync({
              machineUuid: machine.machineUuid,
              auraAccess: machine.auraAccess,
            }),
          );
        }

        return promises;
      });

      const results = await Promise.allSettled(updates);
      const failures = results.filter((result) => result.status === "rejected");

      if (failures.length === 0) {
        toast("SmartParks updated successfully", { variant: "success" });
        // Delay invalidation to next tick to allow commitSave to run first
        setTimeout(() => {
          // Invalidate all queries for this endpoint, regardless of region filter
          const baseOptions = isTaggingMachines
            ? getMachinesSearchTaggingOptions()
            : getMachinesSearchOptions();

          queryClient.invalidateQueries({
            queryKey: [baseOptions.queryKey[0]],
          });
        }, 0);
      } else if (failures.length === results.length) {
        toast("All updates failed. Please try again.", { variant: "error" });
      } else {
        toast("Some updates failed. Please try again.", { variant: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full py-6 px-16 font-manrope">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {showRegionFilter && (
          <div className="flex flex-col gap-2">
            <Label>Filter by Region</Label>
            <Select
              items={regionOptions}
              value={selectedRegion}
              onValueChange={setSelectedRegion}
              placeholder="All Regions"
            />
          </div>
        )}

        {showSearchFilter && (
          <div className="flex flex-col gap-2">
            <Label>Search by Name</Label>
            <Input
              type="text"
              placeholder="Search SmartParks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
      </div>

      {loadingMachines || isFetchingNextPage ? (
        <div className="flex justify-center items-center py-12">
          <Spinner />
        </div>
      ) : (
        <EditableTable
          data={paginatedMachines}
          columns={columns}
          keyExtractor={(row) => row.machineUuid}
          onSave={handleSave}
          isSaving={isSaving}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredMachines.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          itemLabel="SmartParks"
        />
      )}
    </div>
  );
};

export default ManageSystemsPage;
